import { reaction } from 'mobx'
import { types, flow, getRoot, getParent, getSnapshot } from 'mobx-state-tree'
import Router from 'next/router'

import io from 'socket.io-client'
import buildCustomValidationStore from '~/stores/models/customValidation'

import { VA_REGIONS } from '~/components/traffic/lookup/Forms/Elements/CourtSelector'
import * as Delays from '~/utilities/delays'
import objectIsEmpty from '~/utilities/objectIsEmpty'
import * as LocalStorage from '~/utilities/localStorage'
import {
  loadFlowState,
  saveFlowState,
  clearFlowState,
} from '~/utilities/trafficLookupFlowPersistence'
import * as StringTools from '~/utilities/string'
import { userInputFormatDate } from '~/utilities/dates'
import { chargeTrafficTicket, overwritePaymentProcessor } from '~/services/payments'
import { trackMetaBrowserPurchase } from '~/utilities/metaCapiBrowserEvents'
import * as TrafficTicketService from '~/services/trafficTicket'
import { unsubscribeByFetcherSearchContactInfo } from '~/services/fetcher'
import TRACKING from '~/services/tracking'
import * as TrafficStateService from '~/services/trafficState'
import TrafficState from '~/stores/trafficTicket/models/TrafficState'
import TrafficTicket from '~/stores/trafficTicket/models/TrafficTicket'

import {
  REQUEST_STATUSES,
  TRAFFIC_STATUS_ORDER,
  TRAFFIC_STATUS,
  FETCHER_NAMES_TITLES,
  FETCHER_CITY_STATES,
  STATES,
  FETCHER_TIMEOUT_DURATIONS,
  PAYMENT_PROVIDERS,
  IS_FETCHER_COMBINED,
} from '~/utilities/constants'
import {
  SEARCH_OPTIONS,
  SEARCH_OPTIONS_STATES,
  SEARCH_OPTIONS_VALIDATION_KEYS,
  USER_INFO_SEARCH_OPTIONS,
  SEARCH_OPTIONS_STATES_IS_NYS_LICENSE,
} from '~/components/traffic/lookup/Forms/Elements/searchConfig'
import {
  SEARCH_FORM_KEYS,
  TRAFFIC_TICKET_KEYS_TO_SAVE,
  NYDMV_SUBMIT_KEYS_NY,
  NYDMV_SUBMIT_KEYS_NON_NY,
  NYDMV_PAYLOAD_KEYS,
  getUserSessionToken,
  FETCHER_VALIDATIONS,
  FETCHER_ASYNC_VALIDATIONS,
} from './lookupConfig'
import FetcherTicket from './models/FetcherTicket'

const TrafficLookupStore = types
  .model('TrafficLookupStore', {
    manualTrafficTicket: types.maybeNull(TrafficTicket),
    manualTrafficTicketError: types.optional(types.string, ''),
    storeName: 'lookupStore',
    // license state (default NY for NY lookup form)
    licenseState: types.frozen({
      shortName: 'NY',
      longName: 'New York',
      available: true,
      prospect: true,
    }),
    trafficStates: types.array(TrafficState),
    fetcherState: types.optional(types.string, ''),
    fetcherName: types.optional(types.string, ''),
    cameFromDOTEmail: types.optional(types.boolean, false),
    isNYSLicense: types.optional(types.boolean, true),
    searchUsingOtherInfo: types.optional(types.boolean, false),
    searchUsingOtherInfoTouched: types.optional(types.boolean, false),
    /** Nassau: 'plate' | 'license' - which search tab is active */
    nassauSearchTab: types.optional(types.enumeration(['plate', 'license']), 'license'),
    // two search column configs
    columnOne: types.optional(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
      SEARCH_OPTIONS.NYSLicense,
    ),
    columnTwo: types.optional(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
      SEARCH_OPTIONS.lastFourSSN,
    ),
    columnThree: types.maybeNull(
      types.enumeration(Object.keys(SEARCH_OPTIONS)),
    ),
    // form inputs
    licenseID: '',
    licenseBack: '',
    plateNumber: '',
    SSNNumber1: '',
    SSNNumber2: '',
    SSNNumber3: '',
    SSNNumber4: '',
    zipCode: '',
    gender: 'male',
    DOBString: '',
    firstName: '',
    middleName: '',
    lastName: '',
    trafficTicketNumber: '',
    violateDate: '',
    // initialized: false,
    // statusText: '',
    canProgress: true,
    fetcherTickets: types.array(FetcherTicket),
    fetchingTicketsStatus: types.optional(types.string, REQUEST_STATUSES.init),
    selectedTrafficTicket: types.maybeNull(types.reference(FetcherTicket)),
    fullName: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    phoneNumber: types.optional(types.string, ''),
    source: types.optional(types.string, ''),
    unsubscribed: types.optional(types.boolean, false),
    popupIsOpen: types.optional(types.boolean, false),
    isStateAffiliatedWithWinitLaw: types.optional(types.boolean, false),
    useNYDMVForm: types.optional(types.boolean, false),
    /** Raw scan result from license scan when successful (optional, not required) */
    licenseRaw: types.maybeNull(types.frozen()),
    hasSentFetcherInfoAdded: types.optional(types.boolean, false),
    courtFilter: types.optional(types.string, ''),
    // Not part of persistableSlice on purpose — must reset to false on every
    // fresh store instance so results.jsx knows whether a restore attempt
    // has already run this mount (see restoreFlowState below).
    hasAttemptedRestore: types.optional(types.boolean, false),
  })
  .views((self) => ({
    get searchData() {
      const response = {}
      // For NYDMV form send as much data as we have (both NY and non-NY fields)
      const payloadKeys = self.useNYDMVForm ? NYDMV_PAYLOAD_KEYS : self.searchFormKeys
      payloadKeys.forEach((key) => {
        switch (key) {
          case 'SSN':
            response.last4SSN = self.SSN
            break
          default:
            response[key] = self[key]
            break
        }
      })
      if (self.licenseRaw != null) {
        response.licenseRaw = self.licenseRaw
      }
      // Always send ticket & violate date for NYDMV (incl. NY license) so backend saves them to FetcherSearchCriteria
      if (self.useNYDMVForm) {
        response.trafficTicketNumber = self.trafficTicketNumber
        response.violateDate = self.violateDate
      }
      response.contactInfo = self.contactInfo
      response.state = self.fetcherState
      response.fetcherName = self.fetcherName

      // Nassau: tell backend which search method (plate vs license)
      if (self.fetcherName === 'NY_NASSAU') {
        response.nassauSearchMethod = self.nassauSearchTab
        // Derive lastName from fullName for license search
        if (self.nassauSearchTab === 'license' && self.fullName) {
          const parts = self.fullName.trim().split(/\s+/)
          response.lastName = parts.length ? parts[parts.length - 1] : ''
        }
      }


      // VA: expand selected region to its courts
      if (self.fetcherName === 'VA' && self.courtFilter) {
        response.courts = VA_REGIONS[self.courtFilter] || [self.courtFilter]
      }

      // If we have utm parameters from an anonymous user (not logged in), set the utm in contactInfo from AuthStore
      // Otherwise, set the utm object from the user. If the user is logged in and has arrived with UTM params,
      // they will be available on auth.user.utm (see handleUtmParameters() in _app.jsx).
      const { auth } = getRoot(self)
      if (!objectIsEmpty(auth.utmParameters)) {
        response.contactInfo.utm = auth.utmParameters
      } else {
        response.contactInfo.utm = auth.user.utm
      }
      const gclid = auth.gclid || auth.user.gclid
      if (gclid) {
        response.contactInfo.gclid = gclid
      }

      return response
    },
    get renderAttorneySelector() {
      if (
        (self.selectedTrafficTicket && self.selectedTrafficTicket.quotes.length <= 1) ||
        self.isStateAffiliatedWithWinitLaw
      ) {
        return false
      }
      return true
    },
    get SSN() {
      return self.SSNNumber1 + self.SSNNumber2 + self.SSNNumber3 + self.SSNNumber4
    },
    get totalPrice() {
      if (self.selectedTrafficTicket.fetcherTrafficTicket.quote.pricing) {
        return self.selectedTrafficTicket.fetcherTrafficTicket.quote.pricing.winItPrice
      }
      return 0
    },
    get allUniqueFAQs() {
      if (self.fetcherTickets && self.fetcherTickets.length) {
        const seenQuestions = {}
        return self.fetcherTickets
          .reduce((accum, item) => {
            const currentFAQ = item.allQuoteFAQs
            return accum.concat(currentFAQ)
          }, [])
          .filter((item) => {
            if (seenQuestions[item[0]]) {
              return false
            }
            seenQuestions[item[0]] = true
            return true
          })
      }
      return []
    },
    get searchFormKeys() {
      if (self.useNYDMVForm && self.isNYSLicense) {
        return NYDMV_SUBMIT_KEYS_NY
      }
      if (self.useNYDMVForm && !self.isNYSLicense) {
        return NYDMV_SUBMIT_KEYS_NON_NY
      }
      if (self.useNYDMVForm) {
        return SEARCH_FORM_KEYS
      }
      // Nassau: use license tab keys when that tab is active (no userInfo - not shown for Nassau)
      if (self.fetcherName === 'NY_NASSAU' && self.nassauSearchTab === 'license') {
        return SEARCH_OPTIONS_VALIDATION_KEYS[SEARCH_OPTIONS.NassauLicenseId]
      }
      const columnOne = SEARCH_OPTIONS_VALIDATION_KEYS[self.columnOne]
      const columnTwo = SEARCH_OPTIONS_VALIDATION_KEYS[self.columnTwo]
      const columnThree = self.columnThree ? SEARCH_OPTIONS_VALIDATION_KEYS[self.columnThree] ?? [] : []
      let userInputFields = Object.keys(USER_INFO_SEARCH_OPTIONS)
      if (self.columnOne === 'nameDOBGender' || self.columnOne === 'Name' || self.columnThree === 'Name') {
        userInputFields = userInputFields.filter((val) => val !== 'fullName')
      }
      return [...columnOne, ...columnTwo, ...columnThree, ...userInputFields]
    },
    get searchFormHasErrors() {
      return self.validateKeys(self.searchFormKeys)
    },
    get fightableTickets() {
      return self.fetcherTickets.filter((ticket) => ticket.isFightable)
    },
    get contactInfo() {
      if (self.useNYDMVForm) {
        return {
          fullName: self.fullName,
          email: self.email,
          phoneNumber: self.phoneNumber,
        }
      }
      if (self.columnOne === 'nameDOBGender' || self.columnOne === 'Name' || self.columnThree === 'Name') {
        return {
          fullName: `${self.firstName} ${
            self.middleName ? `${self.middleName} ` : ''
          }${self.lastName}`.trim(),
          email: self.email,
          phoneNumber: self.phoneNumber,
        }
      }
      return {
        fullName: self.fullName,
        email: self.email,
        phoneNumber: self.phoneNumber,
      }
    },
    allowCreateManualTrafficTicket() {
      const ticket = self.manualTrafficTicket
      if (ticket.ticketID.length === 0 && ticket.validateKey('persistedTicketImage')) {
        return false
      }
      if (ticket.validateKey('ticketID')) {
        return false
      }
      return true
    },
    getLookupFormSnapshot() {
      return {
        fullName: self.fullName,
        email: self.email,
        phoneNumber: self.phoneNumber,
        licenseID: self.licenseID,
        DOBString: self.DOBString,
        zipCode: self.zipCode,
        trafficTicketNumber: self.trafficTicketNumber,
        violateDate: self.violateDate,
        licenseBack: self.licenseBack,
        gender: self.gender,
        licenseStateShortName: self.licenseState?.shortName,
        ssn: self.SSN,
        licenseRaw: self.licenseRaw,
      }
    },
    // Minimal, serializable slice needed to rebuild results/attorney/checkout
    // after a refresh. Full snapshot per ticket — fightability, status, and
    // quotes are needed as-is by the results/attorney/checkout pages, so a
    // hand-picked subset would leave restored tickets mis-rendered.
    // View (not action) so the autosave reaction tracks its dependencies.
    get persistableSlice() {
      return {
        fetcherTickets: self.fetcherTickets.map((ticket) => getSnapshot(ticket)),
        selectedTicketId: self.selectedTrafficTicket ? self.selectedTrafficTicket.ticketID : null,
        fetcherName: self.fetcherName,
        fetcherState: self.fetcherState,
        isNYSLicense: self.isNYSLicense,
        useNYDMVForm: self.useNYDMVForm,
        columnOne: self.columnOne,
        columnTwo: self.columnTwo,
        columnThree: self.columnThree,
        nassauSearchTab: self.nassauSearchTab,
        courtFilter: self.courtFilter,
        source: self.source,
        fetchingTicketsStatus: self.fetchingTicketsStatus,
        isStateAffiliatedWithWinitLaw: self.isStateAffiliatedWithWinitLaw,
        hasSentFetcherInfoAdded: self.hasSentFetcherInfoAdded,
      }
    },
  }))
  .actions((self) => ({
    afterCreate() {
      // Todo: this needs a better name - currently it's far too abstract.
      self.manualTrafficTicket = TrafficTicket.create()
      if (process.browser) {
        reaction(
          () => JSON.stringify(self.getLookupFormSnapshot()),
          () => self.persistLookupFormToStorage(),
          { delay: 500 }
        )
        // Persist the flow slice to sessionStorage whenever relevant state
        // changes (debounced 500ms), so results/attorney/checkout/success can
        // rebuild themselves after a hard refresh instead of bouncing the
        // user back to the first lookup page. Mirrors the parking flow
        // (utilities/flowPersistence.js).
        reaction(
          () => JSON.stringify(self.persistableSlice),
          () => saveFlowState(self.persistableSlice),
          { delay: 500 }
        )
      }
    },
    // Rebuild the lookup flow from a saved slice. Synchronous (tickets are
    // restored from stored snapshots, no network). Returns false if there is
    // nothing to restore so the caller (page guard) can bounce to the start
    // of the lookup flow instead.
    restoreFlowState() {
      if (!process.browser) return false
      // Set before the lookup below (and regardless of outcome) so the
      // results page can tell "haven't checked sessionStorage yet" (still
      // show a loading state) apart from "checked, there's genuinely
      // nothing" (show the no-tickets empty state).
      self.hasAttemptedRestore = true
      const slice = loadFlowState()
      if (!slice || !Array.isArray(slice.fetcherTickets) || !slice.fetcherTickets.length) {
        return false
      }
      self.fetcherTickets = slice.fetcherTickets
      if (slice.fetcherName) self.fetcherName = slice.fetcherName
      if (slice.fetcherState) self.fetcherState = slice.fetcherState
      self.isNYSLicense = !!slice.isNYSLicense
      self.useNYDMVForm = !!slice.useNYDMVForm
      if (slice.columnOne) self.columnOne = slice.columnOne
      if (slice.columnTwo) self.columnTwo = slice.columnTwo
      self.columnThree = slice.columnThree || null
      self.nassauSearchTab = slice.nassauSearchTab || 'license'
      self.courtFilter = slice.courtFilter || ''
      self.source = slice.source || ''
      self.isStateAffiliatedWithWinitLaw = !!slice.isStateAffiliatedWithWinitLaw
      self.hasSentFetcherInfoAdded = !!slice.hasSentFetcherInfoAdded
      if (slice.fetchingTicketsStatus) self.fetchingTicketsStatus = slice.fetchingTicketsStatus
      if (slice.selectedTicketId) {
        const match = self.fetcherTickets.find((ticket) => ticket.ticketID === slice.selectedTicketId)
        // Assign directly rather than via setSelectedTrafficTicket(), which
        // force-selects quotes[0] — that would silently revert an attorney
        // the user already picked (fetcherTrafficTicket.quote is restored
        // as-is from the snapshot, so it already reflects their choice).
        if (match) self.selectedTrafficTicket = match
      }
      return true
    },
    // Called once the flow reaches a terminal step (success / manual
    // submission) so a refresh there does not rehydrate a completed
    // submission. The autosave reaction stays alive for the next search.
    clearFlowSession() {
      if (!process.browser) return
      clearFlowState()
    },
    prepopulateUserInfo() {
      const { user } = getRoot(self).auth
      this.setEmail(user.email)
      this.setFullName(user.name)
      this.setPhoneNumber(user.phoneNumber)
      this.setLicenseID(user.licenseID)
      if (process.browser && (!user.name || !user.email || !user.phoneNumber || !user.licenseID)) {
        const saved = LocalStorage.get('trafficLookupForm', true)
        if (saved) {
          if (!user.name && saved.fullName) this.setFullName(saved.fullName)
          if (!user.email && saved.email) this.setEmail(saved.email)
          if (!user.phoneNumber && saved.phoneNumber) this.setPhoneNumber(saved.phoneNumber)
          if (!user.licenseID && saved.licenseID) this.setLicenseID(saved.licenseID)
        }
      }
    },
    persistLookupFormToStorage() {
      if (!process.browser) return
      try {
        LocalStorage.set('trafficLookupForm', JSON.stringify(self.getLookupFormSnapshot()))
      } catch (_) {}
    },
    restoreLookupFormFromStorage() {
      if (!process.browser) return
      try {
        const saved = LocalStorage.get('trafficLookupForm', true)
        if (!saved || typeof saved !== 'object') return
        if (saved.fullName != null) self.fullName = String(saved.fullName)
        if (saved.email != null) self.email = String(saved.email)
        if (saved.phoneNumber != null) self.phoneNumber = String(saved.phoneNumber)
        if (saved.licenseID != null) self.licenseID = String(saved.licenseID)
        if (saved.DOBString != null) self.DOBString = String(saved.DOBString)
        if (saved.zipCode != null) self.zipCode = String(saved.zipCode).replace(/\D/g, '').slice(0, 5)
        if (saved.trafficTicketNumber != null) self.trafficTicketNumber = String(saved.trafficTicketNumber).toUpperCase()
        if (saved.violateDate != null) self.violateDate = String(saved.violateDate)
        if (saved.licenseBack != null) self.licenseBack = String(saved.licenseBack).toUpperCase()
        if (saved.gender === 'female' || saved.gender === 'male') self.gender = saved.gender
        if (saved.ssn != null) {
          const str = String(saved.ssn).replace(/\D/g, '').slice(0, 4)
          if (str.length === 4) {
            self.SSNNumber1 = str[0]
            self.SSNNumber2 = str[1]
            self.SSNNumber3 = str[2]
            self.SSNNumber4 = str[3]
          }
        }
        if (saved.licenseStateShortName && self.trafficStates && self.trafficStates.length > 0) {
          self.setLicenseState({ value: saved.licenseStateShortName })
        }
        if (saved.licenseRaw != null && typeof saved.licenseRaw === 'object') {
          self.licenseRaw = saved.licenseRaw
        }
      } catch (_) {}
    },
    setEmail(email) {
      self.email = email
    },
    setFullName(fullName) {
      self.fullName = fullName
    },
    setPhoneNumber(phoneNumber) {
      self.phoneNumber = phoneNumber
    },
    setLicenseID(licenseID) {
      self.licenseID = licenseID
    },
    openPopup() {
      self.popupIsOpen = true
    },
    closePopup() {
      self.popupIsOpen = false
    },
    setLicenseState(newStateOption) {
      const target = self.trafficStates.filter((item) => item.shortName === newStateOption.value)
      if (!target || !target.length) return false
      self.licenseState = {
        shortName: target[0].shortName,
        longName: target[0].longName,
        available: target[0].available,
        prospect: target[0].prospect,
      }
      self.isNYSLicense = self.licenseState.shortName === 'NY'
      // Form columns are always determined by the fetcher, not by the selected license state
      const fetcherConfig = SEARCH_OPTIONS_STATES[self.fetcherName]
      if (fetcherConfig) {
        self.columnOne = fetcherConfig.columnOne
        self.columnTwo = fetcherConfig.columnTwo
        self.columnThree = fetcherConfig.columnThree ?? null
      } else {
        self.columnOne = SEARCH_OPTIONS.nameDOBGender
        self.columnTwo = SEARCH_OPTIONS.ticketNumber
        self.columnThree = null
      }
      return self.licenseState
    },
    loadTrafficStates: flow(function* fetchStates() {
      try {
        const trafficStates = yield TrafficStateService.getAll()
        self.setTrafficStates(trafficStates)
      } catch (error) {
        console.error('Error fetching the states.')
      }
    }),
    checkStateAffiliatedWithWinitLaw(stateShortName) {
      try {
        switch (stateShortName) {
          case 'CAL':
          case 'CAL_STATE':
          case 'CAL_SANTA_BARBARA':
          case 'CAL_ORANGE':
          case 'CAL_SAN_FRANCISCO':
          case 'CAL_VENTURA':
          case 'CAL_SAN_BERNARDINO':
          case 'CAL_SAN_DIEGO':
          case 'CAL_SANTA_CLARA':
          case 'CAL_KERN':
            stateShortName = 'CA'
            break
          case 'GA':
            stateShortName = 'GA'
            break
          case 'FL_MIAMI':
            stateShortName = 'FL'
            break
          case 'WA_NEW_SEATTLE':
            stateShortName = 'WA'
            break
          case 'PA_PHILADELPHIA':
            stateShortName = 'PA'
            break
          case 'NY_NASSAU':
          case 'NY_SUFFOLK':
          case 'NY_WESTCHESTER':
            stateShortName = 'NY'
            break
          case 'NY_CRIMINAL':
          case 'NY_STATE':
            stateShortName = 'NY'
            break
          case 'TX_AUSTIN':
            stateShortName = 'TX'
            break
          case 'CAL_SAN_JOAQUIN':
            stateShortName = 'CA'
            break
          default:
            break
        }

        const trafficState = self.trafficStates.filter((item) => item.shortName === stateShortName)
        if (!trafficState || !trafficState.length) return false

        return trafficState[0].affiliatedWithWinitLaw
      } catch (error) {
        console.error('Error fetching affiliatedWithWinitLaw flag for state', stateShortName)
      }
    },
    setTrafficStates(statesArray) {
      self.trafficStates = statesArray
    },
    setIsStateAffiliatedWithWinitLaw(stateShortName) {
      switch (stateShortName) {
        case 'CAL':
        case 'CAL_STATE':
        case 'CAL_SANTA_BARBARA':
        case 'CAL_ORANGE':
        case 'CAL_SAN_FRANCISCO':
        case 'CAL_VENTURA':
        case 'CAL_SAN_BERNARDINO':
        case 'CAL_SAN_DIEGO':
        case 'CAL_SANTA_CLARA':
        case 'CAL_KERN':
          stateShortName = 'CA'
          break
        case 'GA':
          stateShortName = 'GA'
          break
        case 'FL_MIAMI':
          stateShortName = 'FL'
          break
        case 'WA_NEW_SEATTLE':
          stateShortName = 'WA'
          break
        case 'PA_PHILADELPHIA':
          stateShortName = 'PA'
          break
        case 'NY_NASSAU':
        case 'NY_SUFFOLK':
        case 'NY_WESTCHESTER':
          stateShortName = 'NY'
          break
        case 'NY_CRIMINAL':
          stateShortName = 'NY'
          break
        case 'TX_AUSTIN':
          stateShortName = 'TX'
          break
        case 'CAL_SAN_JOAQUIN':
          stateShortName = 'CA'
          break
        default:
          break
      }
      try {
        const trafficState = self.trafficStates.filter((item) => item.shortName === stateShortName)
        if (!trafficState) console.error('Error fetching traffic state for ', stateShortName)
        self.isStateAffiliatedWithWinitLaw = trafficState[0].affiliatedWithWinitLaw
      } catch (error) {
        console.error('Error fetching affiliatedWithWinitLaw flag for state', stateShortName)
        self.isStateAffiliatedWithWinitLaw = false
      }
    },
    /**
     * For NY DMV multi-step form: fire a lightweight socket.io call with the current
     * search criteria so the backend can persist / observe partial data.
     * This does NOT listen for results and does NOT trigger any tracking conversions.
     */
    savePartialSearchCriteria(stepLabel) {
      if (!process.browser) return
      try {
        const url = process.env.PARSE_SERV_URL
        const socket = io(url, {
          transports: ['websocket'],
        })
        const searchCriteria = {
          ...self.searchData,
          partial: true,
          telemetry: {
            origin_create: 'v2 web app traffic lookup flow – partial save',
            created_source: 'regular_flow',
            lookup_page: 'traffic-lookup',
            step: stepLabel,
            fetcher_name: self.fetcherName,
          },
        }
        socket.on('connect', () => {
          socket.emit('getFetcherTickets', searchCriteria)
          // We don't care about results; close shortly after emit to avoid leaks.
          setTimeout(() => {
            try {
              socket.close()
            } catch (_) {}
          }, 5000)
        })
      } catch (error) {
        console.error('Error saving partial NY DMV search criteria', error)
      }
    },
    validateForm: flow(function* (keys) {
      self.resetTouched()
      self.resetDisplayErrors()

      const keysToValidate = keys || self.searchFormKeys
      self.validateKeyList(keysToValidate)
      yield self.asyncValidateKeyList(keysToValidate)
    }),
    // FORM INPUTS
    changeLicenseIDNumber(newLicenseIdNumber) {
      if (
        !newLicenseIdNumber.length ||
        (newLicenseIdNumber.length && /^[a-zA-Z0-9]*$/.test(newLicenseIdNumber))
      ) {
        self.addTouched('licenseID')
        self.licenseID = newLicenseIdNumber.toUpperCase()
      }
    },
    changeLicenseBack(value) {
      self.addTouched('licenseBack')
      self.licenseBack = String(value ?? '').toUpperCase()
    },
    setLicenseRaw(raw) {
      if (raw == null) {
        self.licenseRaw = null
        return
      }
      if (typeof raw !== 'object') {
        self.licenseRaw = raw
        return
      }
      const omit = [
        'pdf417',
        'middleNameTruncation',
        'firstNameTruncation',
        'lastNameTruncation',
        'hairColor',
        'version',
      ]
      const copy = { ...raw }
      omit.forEach((k) => delete copy[k])
      self.licenseRaw = copy
    },
    markFetcherInfoAdded() {
      self.hasSentFetcherInfoAdded = true
    },
    changeZipCode(newZipCode) {
      self.addTouched('zipCode')
      self.zipCode = newZipCode
    },
    changePlateNumber(newPlateNumber) {
      if (
        !newPlateNumber.length ||
        (newPlateNumber.length && /^[a-zA-Z0-9]*$/.test(newPlateNumber))
      ) {
        self.addTouched('plateNumber')
        self.plateNumber = newPlateNumber.toUpperCase()
      }
    },
    setFetcherName(fetcherName) {
      self.fetcherName = fetcherName
      if (fetcherName !== 'NY_NASSAU') {
        self.nassauSearchTab = 'license'
      }
      self.isNYSLicense = SEARCH_OPTIONS_STATES_IS_NYS_LICENSE[self.fetcherName]
      self.setColumn('columnOne', SEARCH_OPTIONS_STATES[self.fetcherName].columnOne)
      self.setColumn('columnTwo', SEARCH_OPTIONS_STATES[self.fetcherName].columnTwo)
      if (SEARCH_OPTIONS_STATES[self.fetcherName].columnThree) {
        self.setColumn('columnThree', SEARCH_OPTIONS_STATES[self.fetcherName].columnThree)
      } else {
        self.columnThree = null
      }
    },
    setFetcherState(fetcherState) {
      self.fetcherState = fetcherState
    },
    addLast4SSN(name, newValue) {
      // TODO: refactor to make SSN one string
      self.addTouched('SSN')
      self[name] = newValue
    },
    deleteLast4SSN(name) {
      // TODO: refactor to make SSN one string
      self.addTouched('SSN')
      self[name] = ''
    },
    setLast4SSN(value) {
      const str = String(value).replace(/\D/g, '').slice(0, 4)
      self.addTouched('SSN')
      self.SSNNumber1 = str[0] || ''
      self.SSNNumber2 = str[1] || ''
      self.SSNNumber3 = str[2] || ''
      self.SSNNumber4 = str[3] || ''
    },
    changeDOBString(newDOBString) {
      self.addTouched('DOBString')
      self.setDOB(userInputFormatDate(newDOBString))
    },
    setDOB(value) {
      self.DOBString = value
    },
    setSource(value) {
      self.source = value
    },
    setUnsubscribed(value) {
      self.unsubscribed = value
    },
    changeGender(newGender) {
      self.addTouched('gender')
      self.gender = newGender
    },
    changeName(FML, newName) {
      self.addTouched(FML)
      self[FML] = newName
    },
    changeKey(key, value) {
      if (key === 'fullName') {
        self.addTouched('fullName')
        self.fullName = value
      }
    },
    setCourtFilter(value) {
      self.courtFilter = value
    },
    changeTrafficTicketNumber(newTrafficTicketNumber) {
      self.addTouched('trafficTicketNumber')
      self.trafficTicketNumber = newTrafficTicketNumber.toUpperCase()
    },
    changeViolateDate(newViolateDate) {
      self.addTouched('violateDate')
      self.violateDate = userInputFormatDate(newViolateDate)
    },
    setUseNYDMVForm(value) {
      self.useNYDMVForm = !!value
    },
    // search status
    setSearchingTickets(status) {
      self.searchingTickets = status
    },
    // update which element is shown in each column
    setColumn(column, option) {
      // clear out ssn field if switching to ssn
      if (option === SEARCH_OPTIONS.lastFourSSN) {
        self.deleteLast4SSN('SSNNumber1')
        self.deleteLast4SSN('SSNNumber2')
        self.deleteLast4SSN('SSNNumber3')
        self.deleteLast4SSN('SSNNumber4')
      }
      self.resetTouched()
      self.resetDisplayErrors()
      self[column] = option
    },
    customerCameFromDOTEmail() {
      self.cameFromDOTEmail = true
    },
    setIsNYSLicense(value) {
      self.resetTouched()
      self.resetDisplayErrors()
      self.isNYSLicense = !!value
      if (!self.isNYSLicense) {
        self.columnOne = SEARCH_OPTIONS.nameDOBGender
        self.columnTwo = SEARCH_OPTIONS.ticketNumber
        if (self.fetcherName === 'NY_STATE') {
          self.columnThree = SEARCH_OPTIONS.PlateNumberAndLicenseState
        } else {
          self.columnThree = null
        }
      } else {
        self.columnOne = self.fetcherName === 'NY_STATE' ? SEARCH_OPTIONS.LicenseIdAndDob : SEARCH_OPTIONS.NYSLicense
        self.columnTwo = SEARCH_OPTIONS.lastFourSSN
        if (self.fetcherName === 'NY_STATE') {
          self.columnThree = SEARCH_OPTIONS.DOBPlateNumberAndLicenseState
        } else {
          self.columnThree = null
        }
      }
    },
    toggleIsNYSLicense() {
      self.setIsNYSLicense(!self.isNYSLicense)
    },
    toggleSearchUsingOtherInfo() {
      self.searchUsingOtherInfo = !self.searchUsingOtherInfo
    },
    touchedSearchUsingOtherInfo() {
      self.searchUsingOtherInfoTouched = true
      // Cookies.set(TOUCHED_DROPDOWN_COOKIE_NAME, true, { expires: 7, path: '' })
    },
    setNassauSearchTab(tab) {
      self.resetTouched()
      self.resetDisplayErrors()
      self.nassauSearchTab = tab
    },
    getUserFormData() {
      const { user } = getRoot(self).auth
      if (user.objectId) {
        const keys = ['licenseID', 'gender', 'firstName', 'lastName']
        keys.forEach((key) => {
          if (user[key]) {
            self[key] = user[key]
          }
        })
        if (user.dob) {
          self.DOBString = user.dob.split('/').join(' / ')
        }
      }
    },
    setFetcherStatus(status) {
      self.fetchingTicketsStatus = status
    },
    setManualTrafficTicketError(error) {
      self.manualTrafficTicketError = error
    },
    resetSelectedTrafficTicket() {
      self.selectedTrafficTicket = null
    },
    setSelectedTrafficTicket(ticket) {
      self.resetDisplayErrors()
      self.selectedTrafficTicket = ticket
      if (ticket) {
        self.selectedTrafficTicket.fetcherTrafficTicket.selectQuote(
          self.selectedTrafficTicket.quotes[0],
        )
      }
    },
    showErrorToast(message) {
      const all = getParent(self)
      all.ui.toasts.addToast({
        message:
          message ||
          `Sorry something unexpected happened. Please try again or contact us for further support.`,
        type: 'ERROR',
      })
    },
    submitManualTicket: flow(function* createTicketWithImage() {
      self.manualTrafficTicketError = ''
      if (!self.allowCreateManualTrafficTicket()) {
        self.manualTrafficTicketError = 'At least one of the information above must be entered.'
        TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'validation')
        return false
      }
      TRACKING.trafficLookup.manualSubmit(self.fetcherName)
      const { auth, ui } = getRoot(self)
      const MANUAL_TRAFFIC_TICKET_KEYS_TO_SAVE = [
        'ticketID',
        'comment',
        'locationState',
        'trafficRegion',
      ]
      let origin = 'webapp-lookup-manual_submission'
      console.log(`FETCHER STATE ${Router.route}`)
      if (
        Router.route.includes(`/traffic/lookup/no-result`) ||
        Router.route.includes(`/traffic/lookup/${Router.query.state}/no-result`) ||
        Router.route.includes(`/traffic/lookup/[state]/no-result`)
      )
        origin = 'webapp-lookup-pct'
      if (self.fetcherName === 'NY_CRIMINAL') origin = 'webapp-lookup-criminal-manual_submission'
      if (auth.isLoggedIn) {
        // creating the ticket
        try {
          self.manualTrafficTicket.setState(self.fetcherState)
          yield self.manualTrafficTicket.createTicket(MANUAL_TRAFFIC_TICKET_KEYS_TO_SAVE, origin, self.fetcherName)
          self.manualTrafficTicket.uploadTicketImage()
          TRACKING.trafficLookup.manualSubmitted(self.fetcherName)
          self.clearFlowSession()
          Router.push('/traffic/lookup/submitted')
        } catch (err) {
          self.manualTrafficTicketError = err
          TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, err)
          return false
        }
      } else {
        // If the user is not connected, then create a new user from the information input in the lookup (with random password).
        const password = StringTools.random(15)
        auth.user.changeKey('email', self.email)
        auth.user.changeKey('username', self.email)
        auth.user.changeKey('name', self.fullName)
        auth.user.changeKey('phoneNumber', self.phoneNumber)
        auth.user.changeKey('password', password)
        auth.user.changeKey('paymentProcessor', { type: 'braintree' })
        const USER_KEYS_TO_SAVE = [
          'email',
          'username',
          'name',
          'phoneNumber',
          'password',
          'paymentProcessor',
        ]
        const createdUser = yield auth.user.createNewUser({
          keys: [...USER_KEYS_TO_SAVE],
          locationState: self.fetcherState,
        })

        // If we cannot create a user because it already exist, show an error message asking the user to log in (or show the log in pop up)
        const userAlreadyRegistered = auth.user.displayErrors.some((displayError) => {
          return displayError.message === 'This email is already registered.'
        })

        if (!createdUser && userAlreadyRegistered) {
          auth.changeKey('email', self.email) // Populate the user's email in the sign-in form
          auth.user.changeKey('password', '') // Avoid prepopulating the sign-up form with the random password we generated earlier
          ui.setLoginModalShowing(true) // TODO: show the correct modal depending on the user
          TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'login_required')
        } else if (!createdUser && !userAlreadyRegistered) {
          self.manualTrafficTicketError = 'An error occurred. Please try again later.' // To avoid letting know the user that an account is created
          TRACKING.trafficLookup.manualSubmitFailure(self.fetcherName, 'user_create')
          return false
        } else {
          auth.changeKey('email', self.email) // Populate the user's email in the sign-in form
          auth.changeKey('password', password) // Populate the user's email in the sign-in form
          yield auth.login()
          self.manualTrafficTicket.setState(self.fetcherState)
          yield self.manualTrafficTicket.createTicket(MANUAL_TRAFFIC_TICKET_KEYS_TO_SAVE, origin, self.fetcherName)
          self.manualTrafficTicket.uploadTicketImage()
          TRACKING.trafficLookup.manualSubmitted(self.fetcherName)
          self.clearFlowSession()
          Router.push('/traffic/lookup/submitted')
        }
      }
    }),
    switchPaymentProcessor: flow(function* switchPaymentProcessor() {
      self.validateKeyList(['selectedTrafficTicket'])
      const hasError = self.validateKey('selectedTrafficTicket')
      if (hasError) return false

      const root = getRoot(self)
      const { user } = root.auth
      if (user) {
        const { locationState } = self.selectedTrafficTicket.fetcherTrafficTicket
        yield overwritePaymentProcessor(user, locationState).then((res) => {
          if (res.isAffiliatedWithWinitLaw) {
            user.changeKey('paymentProcessor', { type: PAYMENT_PROVIDERS.cko })
          }
        })
      }
    }),
    routeToAttorneySelectorOrCheckout() {
      self.resetTouched()
      self.resetDisplayErrors()
      self.validateKeyList(['selectedTrafficTicket'])
      const hasError = self.validateKey('selectedTrafficTicket')
      if (hasError) return false
      TRACKING.trafficLookup.checkout(self.fetcherName)
      if (!self.renderAttorneySelector) {
        TRACKING.traffic.fightMyTicket('checkout')
        return Router.push('/traffic/lookup/checkout')
      }
      TRACKING.traffic.fightMyTicket('attorney')
      return Router.push('/traffic/lookup/attorney')
    },
    previousStep() {
      Router.push('/traffic/lookup/results')
    },
    nextStep() {
      Router.push('/traffic/lookup/checkout')
    },
    writePropertiesOnUser() {
      const keys = ['gender', 'licenseID', 'SSN']
      const safe = !self.validateKeyList(keys)
      if (safe) {
        const { user } = getRoot(self).auth
        keys.forEach((key) => {
          user.changeKey(key, self[key])
        })
        if (typeof self.licenseState === 'object') {
          user.changeKey('licenceState', self.licenseState.shortName)
        } else {
          user.changeKey('licenceState', self.licenseState)
        }
        user.changeKey('last4ssn', self.SSN)
        user.changeKey('LastFour', self.SSN)
        user.changeKey('dob', self.DOBString)
        user.changeKey('name', self.fullName)
        user.changeKey('email', self.email)
        user.changeKey('username', self.email)
        user.changeKey('phoneNumber', self.phoneNumber)
      }
    },
    addIdentifiersToTickets(result) {
      const ticketsWithIdentifiers = result.filter((ticket) => ticket.fetcherTrafficTicket).map((ticket) => {
        if (self.source === 'DOT') {
          ticket.fetcherTrafficTicket.isDOTTicket = true
        }
        if (ticket.fetcherTrafficTicket.fetcherName === 'NY_CRIMINAL') {
          ticket.fetcherTrafficTicket.isCriminalFetcher = true
        }
        // add ticketID to ticket
        ticket.ticketID = ticket.fetcherTrafficTicket.ticketID
        if (ticket.fetcherTrafficTicket.fetcherName === 'NY_CRIMINAL' || ticket.fetcherTrafficTicket.fetcherName === 'NY_STATE') {
          ticket.charges = ticket.fetcherTrafficTicket.charges
          ticket.defendant = ticket.fetcherTrafficTicket.name
          ticket.hearingDate = ticket.fetcherTrafficTicket.hearingDate
        }
        // add id to quotes
        ticket.quotes =
          ticket.quotes &&
          ticket.quotes.map((quote) => {
            quote.id = ticket.ticketID + quote.objectId
            return quote
          })
        return ticket
      })
      self.fetcherTickets = ticketsWithIdentifiers

      self.fetcherTickets.forEach((fetcherTicket, i) => {
        fetcherTicket.fetcherTrafficTicket.setDefendant(ticketsWithIdentifiers[i].defendant)
      })

      self.resetSelectedTrafficTicket()
      self.selectFirstTicketByDefault()
    },
    selectFirstTicketByDefault() {
      const ticketToSelect = self.findFirstFightableTicket()
      if (ticketToSelect) {
        self.setSelectedTrafficTicket(ticketToSelect)
      }
    },
    findFirstFightableTicket() {
      for (let i = 0; i < self.fetcherTickets.length; i += 1) {
        const ticket = self.fetcherTickets[i]
        if (ticket.isFightable) {
          return ticket
        }
      }
      return null
    },
    getFetcherTicketsFromId: flow(function* getFetcherTicketsFromIdGen(searchObjectId) {
      try {
        const result =
          yield TrafficTicketService.getFetcherTicketsBySearchCriteriaId(searchObjectId)
        if (result.length === 0) {
          throw new Error('No results found.')
        }
        self.addIdentifiersToTickets(result)
      } catch (error) {
        // self.showToast(error, 'ERROR')
        console.error('error fetching tickets: ', error)
      }
    }),
    DOTUnsubscribe: flow(function* DOTUnsubscribeGen() {
      try {
        const { user } = getRoot(self).auth
        self.setSource('DOT')
        yield TrafficTicketService.unsubscribeDOTUser(user.sessionToken)
        self.setUnsubscribed(true)
      } catch (error) {
        console.error('error unsubcribing user: ', error)
      }
    }),
    fetchDOTickets: flow(function* getDOTTicketsGen() {
      try {
        const { user } = getRoot(self).auth
        self.setSource('DOT')
        const results = yield TrafficTicketService.getUserDOTTicketsAndQuotes(user.sessionToken)
        if (results.length === 0) {
          throw new Error('No DOT tickets found.')
        }
        self.addIdentifiersToTickets(results)
      } catch (error) {
        console.error('error fetching DOT tickets: ', error)
      }
    }),
    canDisplayBackButton() {
      return false
    },
    canDisplayNextButton() {
      return true
    },
    showNoResultPage() {
      if (self.fetcherName === 'NY') {
        Router.push('/traffic/lookup/no-result')
      } else {
        const slugFromMap = Object.entries(FETCHER_CITY_STATES.traffic).find(
          ([, config]) => config.fetcherName === self.fetcherName,
        )?.[0]
        const pathName =
          slugFromMap ||
          FETCHER_NAMES_TITLES[self.fetcherName].toLowerCase().replace(/\s+/g, '-')
        Router.push(`/traffic/lookup/[state]/no-result`, `/traffic/lookup/${pathName}/no-result`, {
          shallow: true,
        }) // We need to make sure that the shallow routing is used in order to keep data on this new page
      }
    },
    getFetcherTickets: flow(function* (flowType) {
      self.setSource('fetcher')

      const validationKeys =
        flowType === 'parking'
          ? [
              ...SEARCH_OPTIONS_VALIDATION_KEYS[self.columnOne],
              ...SEARCH_OPTIONS_VALIDATION_KEYS[self.columnTwo],
            ]
          : undefined

      yield self.validateForm(validationKeys)

      if (self.hasDisplayErrors) return false

      if (flowType !== 'parking' && flowType !== 'dot') {
        TRACKING.trafficLookup.search(self.fetcherName)
      }

      const isNYDMVFlow = self.useNYDMVForm === true
      // Fire "information added" conversions:
      // - For legacy/other flows: always here (preserves existing behavior).
      // - For NY DMV flow: only if Step 1 hasn't already sent them.
      if (!isNYDMVFlow || !self.hasSentFetcherInfoAdded) {
        TRACKING.traffic.fetcherInfoAdded(self.fetcherName)
        if (isNYDMVFlow) {
          self.markFetcherInfoAdded()
        }
      }

      // Google analytics timer (search latency)
      const searchTimer = TRACKING.traffic.fetcherResultsTiming(
        'Lookup informations added',
        self.fetcherName,
      )
      searchTimer.start()

      // Timeout for fetcher request
      const timeoutEnabled = process.env.ENABLE_FETCHER_TIMEOUT_DURATION === 'true'
      let isTimedOut = false

      // VA batched requests need a longer timeout since each batch hits the scraper independently
      const isVABatched = self.fetcherName === 'VA' && self.courtFilter
      const timeoutDuration = isVABatched
        ? 120000
        : (FETCHER_TIMEOUT_DURATIONS.traffic[self.fetcherName] || FETCHER_TIMEOUT_DURATIONS.traffic.DEFAULT)
      const timer = setTimeout(() => {
        isTimedOut = true
        if (timeoutEnabled) {
          console.info('Fetcher response took too long => pushing to no-result page.')
          self.showNoResultPage()
        }
      }, timeoutDuration)

      try {
        self.setFetcherStatus(REQUEST_STATUSES.loading)
        Router.prefetch('/traffic/lookup/results')
        const url = process.env.PARSE_SERV_URL
        

        const searchCriteria = self.searchData
        if (flowType === 'parking')
          searchCriteria.telemetry = {
            origin_create: 'v2 web app parking pay flow',
            created_source: 'regular_flow',
            lookup_page: 'parking-lookup',
            fetcher_name: self.fetcherName,
          }
        else if (flowType === 'dot')
          searchCriteria.telemetry = {
            origin_create: 'v2 web app dot flow',
            created_source: 'regular_flow',
            lookup_page: 'dot-lookup',
            fetcher_name: self.fetcherName,
          }
        else
          searchCriteria.telemetry = {
            origin_create: 'v2 web app traffic lookup flow',
            created_source: 'regular_flow',
            lookup_page: 'traffic-lookup',
            fetcher_name: self.fetcherName,
          }
        // VA with a region selected: split courts into small batches to avoid
        // overloading the scraper (rate limiting) while keeping each request fast.
        // First batch runs immediately; remaining batches start after 5s so the
        // backend is not hit with a burst of parallel connections at once.
        const vaCourts = searchCriteria.courts
        if (self.fetcherName === 'VA' && vaCourts && vaCourts.length > 1) {
          const BATCH_SIZE = 3
          const VA_BATCH_STAGGER_MS = 5000
          const batches = []
          for (let i = 0; i < vaCourts.length; i += BATCH_SIZE) {
            batches.push(vaCourts.slice(i, i + BATCH_SIZE))
          }
          let allResults = []
          let completed = 0
          let hasRedirected = false
          const total = batches.length

          const startBatchLookup = (batch) => {
            const batchCriteria = { ...searchCriteria, courts: batch }
            const batchSocket = io(url, { transports: ['websocket'] })

            batchSocket.on('connect', () => {
              batchSocket.emit('getFetcherTickets', batchCriteria)

              batchSocket.on('getFetcherTicketsError', () => {
                console.error('VA court batch lookup failed for:', batch)
                completed += 1
                if (completed === total) {
                  clearTimeout(timer)
                  if (allResults.length > 0) {
                    self.saveUserInfo()
                    self.addIdentifiersToTickets(allResults)
                    if (!hasRedirected) {
                      hasRedirected = true
                      Router.push('/traffic/lookup/results')
                    }
                    TRACKING.traffic.fetcherResults(self.fetcherName)
                    TRACKING.trafficLookup.results(self.fetcherName, allResults.length)
                    searchTimer.finish()
                    Delays.msDelayPromise(1500).then(() => {
                      self.setFetcherStatus(REQUEST_STATUSES.complete)
                    })
                  } else {
                    TRACKING.traffic.fetcherNoResults(self.fetcherName)
                    TRACKING.trafficLookup.noResult(self.fetcherName)
                    self.setFetcherStatus(REQUEST_STATUSES.error)
                    self.showNoResultPage()
                  }
                }
                try { batchSocket.close() } catch (_) {}
              })

              batchSocket.on('getFetcherTicketsResults', (result) => {
                if (Array.isArray(result) && result.length > 0) {
                  allResults = allResults.concat(result)
                  // Show results as soon as we get the first batch
                  if (!hasRedirected) {
                    hasRedirected = true
                    clearTimeout(timer)
                    self.saveUserInfo()
                    self.addIdentifiersToTickets(result)
                    const pathname = Router.pathname || Router.route || ''
                    const stillInLookupFlow = pathname.includes('/traffic/lookup')
                    if (stillInLookupFlow) {
                      Router.push('/traffic/lookup/results')
                    }
                    TRACKING.traffic.fetcherResults(self.fetcherName)
                    TRACKING.trafficLookup.results(self.fetcherName, result.length)
                  } else {
                    // Append additional results that arrived after redirect
                    self.addIdentifiersToTickets(result)
                  }
                }
                completed += 1
                if (completed === total) {
                  clearTimeout(timer)
                  searchTimer.finish()
                  if (allResults.length > 0) {
                    Delays.msDelayPromise(1500).then(() => {
                      self.setFetcherStatus(REQUEST_STATUSES.complete)
                    })
                  } else {
                    TRACKING.traffic.fetcherNoResults(self.fetcherName)
                    TRACKING.trafficLookup.noResult(self.fetcherName)
                    self.setFetcherStatus(REQUEST_STATUSES.error)
                    self.showNoResultPage()
                  }
                }
                try { batchSocket.close() } catch (_) {}
              })
            })
          }

          startBatchLookup(batches[0])
          if (total > 1) {
            setTimeout(() => {
              batches.slice(1).forEach((batch) => startBatchLookup(batch))
            }, VA_BATCH_STAGGER_MS)
          }
        } else {
        // Default: single request (all non-VA flows + VA without region)
        const socket = io(url, {
          transports: ['websocket'],
        })
        socket.on('connect', () => {
          socket.emit('getFetcherTickets', searchCriteria)
          socket.on('getFetcherTicketsError', (error) => {
            clearTimeout(timer)
            TRACKING.traffic.fetcherNoResults(self.fetcherName)
            TRACKING.trafficLookup.noResult(self.fetcherName)
            self.setFetcherStatus(REQUEST_STATUSES.error)
            console.error('error fetching tickets: ', error)
            self.showNoResultPage()
          })

          socket.on('triggerFetcherTicketsConv', () => {
            TRACKING.traffic.fetcherResults(self.fetcherName)
            TRACKING.trafficLookup.results(
              self.fetcherName,
              self.fetcherTickets.length
            )
          })

          socket.on('getFetcherTicketsResults', (result) => {
            clearTimeout(timer)
            const pathname = Router.pathname || Router.route || ''
            const stillInLookupFlow = pathname.includes('/traffic/lookup')
            let shouldRedirectToResults =
              stillInLookupFlow &&
              (self.fetchingTicketsStatus === REQUEST_STATUSES.loading ||
                pathname.includes('/traffic/lookup/results'))
            if(self.fetchingTicketsStatus === REQUEST_STATUSES.error)
              shouldRedirectToResults = false
            try {
              self.saveUserInfo()
              if (result.length === 0) {
                throw new Error('No results found.')
              }
              self.addIdentifiersToTickets(result)
              if (shouldRedirectToResults) {
                Router.push('/traffic/lookup/results')
              }
              if (!IS_FETCHER_COMBINED[self.fetcherName]) {
                TRACKING.traffic.fetcherResults(self.fetcherName)
                TRACKING.trafficLookup.results(self.fetcherName, result.length)
              }
              searchTimer.finish()
              Delays.msDelayPromise(1500).then(() => {
                self.setFetcherStatus(REQUEST_STATUSES.complete)
              })
            } catch (error) {
              TRACKING.traffic.fetcherNoResults(self.fetcherName)
              TRACKING.trafficLookup.noResult(self.fetcherName)
              self.setFetcherStatus(REQUEST_STATUSES.error)
              console.error('error fetching tickets: ', error)
              if (stillInLookupFlow && ((timeoutEnabled && !isTimedOut) || !timeoutEnabled)) {
                self.showNoResultPage()
              }
            }
          })
        })
        }
      } catch (error) {
        clearTimeout(timer)
        TRACKING.traffic.fetcherNoResults(self.fetcherName)
        TRACKING.trafficLookup.noResult(self.fetcherName)
        self.setFetcherStatus(REQUEST_STATUSES.error)
        console.error('error fetching tickets: ', error)
        self.showNoResultPage()
      }
    }),
    SaveOrUpdateTicket: flow(function* handleSaveOrUpdate() {
      try {
        let savedTicket
        const sessionToken = getUserSessionToken(self)
        if (
          self.selectedTrafficTicket.fetcherTrafficTicket.objectId &&
          !self.selectedTrafficTicket.fetcherTrafficTicket.isDOTTicket
        ) {
          if (TRAFFIC_STATUS_ORDER[self.selectedTrafficTicket.fetcherTrafficTicket.status] < 2) {
            self.selectedTrafficTicket.fetcherTrafficTicket.status = TRAFFIC_STATUS.pendingConf
          }
          savedTicket = yield self.selectedTrafficTicket.fetcherTrafficTicket.updateTicket(
            TRAFFIC_TICKET_KEYS_TO_SAVE,
            sessionToken,
          )
        } else {
          self.selectedTrafficTicket.fetcherTrafficTicket.status = TRAFFIC_STATUS.pendingConf
          const rootStore = getRoot(self)
          const { fetcherName } = rootStore.trafficLookup
          if (self.source === 'DOT') {
            savedTicket =
              yield self.selectedTrafficTicket.fetcherTrafficTicket.createTrafficTicketFromDOTTicket(
                TRAFFIC_TICKET_KEYS_TO_SAVE,
                sessionToken,
                'DOT',
                fetcherName,
              )
          } else {
            const keysForTicketCreation = TRAFFIC_TICKET_KEYS_TO_SAVE
            const keysForTicketCreationFromResult = ['attorney', 'searchResultId']
            // For now (26.10.21) we want to create ticket from search result info just for the NJ Lookup Fetcher.
            // This will be extended to other states once it's fully working for NJ
            if (fetcherName === 'NJ') {
              savedTicket =
                yield self.selectedTrafficTicket.fetcherTrafficTicket.createTicketFromSearchResult(
                  keysForTicketCreationFromResult,
                  'fetcher',
                  fetcherName,
                )
            } else {
              savedTicket = yield self.selectedTrafficTicket.fetcherTrafficTicket.createTicket(
                keysForTicketCreation,
                'fetcher',
                fetcherName,
              )
            }
          }
        }
        return savedTicket
      } catch (error) {
        self.showToast(error, 'ERROR')
        console.error('error saving or updating ticket', error)
      }
    }),
    chargeTicket: flow(function* handleChargeTicket(nonce) {
      const root = getRoot(self)
      const { user } = root.auth
      try {
        const sessionToken = getUserSessionToken(self)
        const { ticketID } = self.selectedTrafficTicket.fetcherTrafficTicket

        const result = yield chargeTrafficTicket(null, ticketID, sessionToken)
        if (result && !result.includes('Charge success for ticket')) {
          throw new Error('Issue charging ticket')
        }
        const ticketObjectId = self.selectedTrafficTicket?.fetcherTrafficTicket?.objectId
        const purchaseValue = self.selectedTrafficTicket?.fetcherTrafficTicket?.winItPrice
        if (ticketObjectId) trackMetaBrowserPurchase(ticketObjectId, purchaseValue)
        self.showToast('Ticket Submitted', 'SUCCESS')
        return result
      } catch (error) {
        if (
          [PAYMENT_PROVIDERS.cko, PAYMENT_PROVIDERS.evervault].includes(user.paymentProcessor.type)
        ) {
          getRoot(self).payment.remountFlow()
        }
        console.error('error charging ticket', error)
        self.showToast(error, 'ERROR')
        TRACKING.trafficLookup.unpaid(self.fetcherName, error)
        return false
      }
    }),
    handleCheckout: flow(function* handleCheckoutGen(nonce) {
      Router.prefetch('/traffic/lookup/success')
      const savedOrUpdatedTicket = yield self.SaveOrUpdateTicket()
      let chargedTicket
      if (savedOrUpdatedTicket) {
        chargedTicket = yield self.chargeTicket(nonce)
        if (!chargedTicket) {
          return false
        }
        // should be in processing now.
        self.selectedTrafficTicket.fetcherTrafficTicket.setStatus(TRAFFIC_STATUS.processing)
      } else {
        return false
      }
      // User fields update
      self.saveUserInfo()

      TRACKING.trafficLookup.paid(self.fetcherName)
      TRACKING.traffic.submissionConversion(
        self.selectedTrafficTicket.fetcherTrafficTicket.objectId,
        'fetcher',
        self.fetcherName,
      )
      self.clearFlowSession()
      Router.push('/traffic/lookup/success')
      yield Delays.msDelayPromise(3000)
    }),
    unsubscribeContactInfo: flow(function* unsubContactInfo(objectId) {
      try {
        yield unsubscribeByFetcherSearchContactInfo(objectId)
        return true
      } catch (error) {
        console.error('Error unsubscribing fetcher search contact info', error)
        return false
      }
    }),
    showToast(message, type) {
      const all = getRoot(self)
      all.ui.toasts.addToast({
        message,
        type,
      })
    },
    saveUserInfo() {
      const { user } = getRoot(self).auth
      self.writePropertiesOnUser()
      const keys = [
        'name',
        'email',
        'username',
        'phoneNumber',
        'gender',
        'licenseID',
        'licenseState',
        'last4ssn',
        'LastFour',
        'dob',
        'paymentProcessor',
      ]
      user.updateUser(keys)
      if (process.browser) {
        self.persistLookupFormToStorage()
      }
    },
  }))

const CustomValidation = buildCustomValidationStore({
  validationObject: FETCHER_VALIDATIONS,
  asyncValidationObject: FETCHER_ASYNC_VALIDATIONS,
})

const FetcherModel = types.compose(TrafficLookupStore, CustomValidation)

export default FetcherModel
